import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, finalize, forkJoin, map, of, switchMap } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { BreadcrumbService, EnumObjectExtended, EnumValues, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ProductionCardDyeingCycle } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';
import { RequisitionStatus } from '@app/models/inventory/requisition';
import {
  BatchConsumptionSummaryRow,
  BatchDyeingCycleRow,
  BatchInsert,
  BatchRequisitionRow,
  BatchStatus,
  BatchType,
} from '@app/models/production/batch/batch';
import { BATCH_STATUS_TRANSITIONS, BATCH_UOM, ENTITY_DEF_PRODUCTION_WORK_ORDER } from '@app/models/production/batch/batch.constants';
import { BatchCreateForm } from '@app/models/production/batch/batch-forms';
import { ProductionWorkOrder, ProductionWorkOrderItem } from '@app/models/production/production-work-order';
import { ProductionBatchRoles } from '@app/models/roles/roles-group';
import { TagView } from '@app/models/shared/tag';
import { BatchProxyService } from '@app/proxies/production/batch.proxy.service';
import { ProductionCardDyeingCycleProxyService } from '@app/proxies/production/production-card-dyein-cycle.proxy.service';
import { ProductionWorkOrderProxyService } from '@app/proxies/production/production-work-order.proxy.service';
import { BatchService } from '@app/services/batch.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { LocationPathService } from '@app/services/location-path.service';
import { UserAuthenticationService } from '@app/services/user-authentication.service';
import { getConsumptionVariance } from '@app/utilities/consumption-variance.util';
import { navigateCrossDomain } from '@app/utilities/cross-domain-navigation.util';
import { BatchDyeingCycleModalComponent } from '@app/components/batch/_modals/batch-dyeing-cycle-modal/batch-dyeing-cycle-modal.component';
import { CreateLotItemModalComponent } from '@app/components/batch/_modals/create-lot-item-modal/create-lot-item-modal.component';

@Component({
  selector: 'app-batch-details',
  templateUrl: './batch-details.component.html',
  styleUrl: './batch-details.component.scss',
})
export class BatchDetailsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly enumService = inject(EnumService);
  private readonly batchService = inject(BatchService);
  private readonly modalService = inject(ModalService);
  private readonly batchProxyService = inject(BatchProxyService);
  private readonly pwoProxyService = inject(ProductionWorkOrderProxyService);
  private readonly dyeingCycleProxyService = inject(ProductionCardDyeingCycleProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  readonly locationPathService = inject(LocationPathService);

  isLoading = signal(0);
  isNewRecord = signal(true);
  isSaving = signal(false);

  canEdit = computed(() => AuthService.hasRole(ProductionBatchRoles.ProductionBatchEditor));

  sourcePwo = signal<ProductionWorkOrder | null>(null);
  availableRecipes = computed(() => this.sourcePwo()?.items ?? []);
  selectedRecipeItem = signal<ProductionWorkOrderItem | null>(null);

  statusEnumObject?: EnumObjectExtended = this.enumService.getEnum('BatchStatus', 'productionEnums');
  statusOptions: EnumValues = this.statusEnumObject?.values ?? {};

  status = new FormControl<BatchStatus | null>(null);
  manufacturerLotNumber = new FormControl<string | null>(null, [Validators.required]);
  expiryDate = new FormControl<Date | null>(null);

  createForm = this.fb.group<BatchCreateForm>({
    productDevelopmentNumber: this.fb.control<string | null>(null, [Validators.required]),
    plannedQuantity: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
  });

  batch = this.batchService.batch;
  productionWorkOrder = this.batchService.productionWorkOrder;
  productRecipe = this.batchService.productRecipe;
  clientPOAllocations = this.batchService.clientPOAllocations;
  requisitions = this.batchService.requisitions;
  dyeingCycles = this.batchService.dyeingCycles;
  lotItems = this.batchService.lotItems;

  availableStatuses = computed<EnumValues>(() => {
    const batch = this.batch();
    const allowed = batch ? (BATCH_STATUS_TRANSITIONS[batch.status] ?? [batch.status]) : [BatchStatus.Draft];
    return Object.fromEntries(Object.entries(this.statusOptions).filter(([key]) => allowed.includes(Number(key) as BatchStatus)));
  });

  canChangeStatus = computed(() => !this.isNewRecord() && this.canEdit() && Object.keys(this.availableStatuses()).length > 1);

  canEditBatchFields = computed(() => {
    const batch = this.batch();
    if (!this.canEdit() || !batch || this.lotItems().length > 0) return false;
    return !batch.isManufacturerLotNumberLocked && !batch.isExpiryDateLocked;
  });

  // emitEvent: false keeps the load from firing valueChanges, which would open the status confirmation dialog.
  private readonly syncFormFromBatch = effect(() => {
    const batch = this.batch();
    if (!batch) return;
    this.status.setValue(batch.status, { emitEvent: false });
    this.manufacturerLotNumber.setValue(batch.manufacturerLotNumber ?? null, { emitEvent: false });
    this.expiryDate.setValue(batch.expiryDate ? new Date(batch.expiryDate) : null, { emitEvent: false });
  });

  private readonly syncBatchFieldsDisabledState = effect(() => {
    if (this.canEditBatchFields()) {
      this.manufacturerLotNumber.enable({ emitEvent: false });
      this.expiryDate.enable({ emitEvent: false });
    } else {
      this.manufacturerLotNumber.disable({ emitEvent: false });
      this.expiryDate.disable({ emitEvent: false });
    }
  });

  // Resolve the full location path of every lot shown in the Requisitions and Lot Items tables.
  private readonly loadLocationPaths = effect(() => {
    const requisitionLotCodes = this.requisitions()
      .flatMap((req) => req.lotItems ?? [])
      .map((lot) => lot.currentLocationCode);
    const lotItemCodes = this.lotItems().map((item) => item.currentLocationCode);
    this.locationPathService.load([...requisitionLotCodes, ...lotItemCodes]);
  });

  title = computed(() => {
    if (this.isNewRecord()) return this.localeHelperService.translateWithFallback('batchDetails.newBatch', 'New Batch');
    const batch = this.batch();
    if (!batch) return this.localeHelperService.translateWithFallback('batchDetails.title', 'Batch Details');
    return this.localeHelperService.translateWithFallback('batchDetails.batchTitle', `Batch : ${batch.number}`, { number: batch.number });
  });

  private readonly syncBreadcrumb = effect(
    () => {
      this.breadcrumbService.setCurrent(this.isNewRecord() ? 'Common_New' : this.batch()?.number);
    },
    { allowSignalWrites: true },
  );

  totalWaterUsage = computed(() =>
    this.dyeingCycles()
      .filter((cycle) => cycle.consumptionsFinalized)
      .reduce((sum, cycle) => sum + (cycle.waterUsageInLiters ?? 0), 0),
  );

  totalItemConsumptions = computed(() =>
    this.dyeingCycles()
      .filter((cycle) => cycle.consumptionsFinalized)
      .reduce((sum, cycle) => sum + (cycle.itemConsumptions ?? []).reduce((cycleSum, item) => cycleSum + item.quantity, 0), 0),
  );

  totalActual = computed(() => this.totalItemConsumptions() + this.totalWaterUsage());

  private estimatedCycleId = computed(
    () => this.dyeingCycles().find((cycle) => cycle.isFirstRun && cycle.estimatedWaterUsageInLiters != null)?.id ?? null,
  );

  private consumptionEstimate = toSignal(
    toObservable(this.estimatedCycleId).pipe(
      switchMap((cycleId) =>
        cycleId
          ? this.dyeingCycleProxyService.getConsumptionEstimate(cycleId).pipe(
              map((response) => response?.data ?? null),
              catchError(() => of(null)),
            )
          : of(null),
      ),
    ),
    { initialValue: null },
  );

  private estimatedItemConsumptions = computed(() => {
    const estimate = this.consumptionEstimate();
    return estimate ? estimate.itemConsumptions.reduce((sum, item) => sum + (item.estimatedQuantity ?? 0), 0) : null;
  });

  private estimatedWaterUsage = computed(() => this.consumptionEstimate()?.estimatedWaterUsageInLiters ?? null);

  private hasFinalizedConsumptions = computed(() => this.dyeingCycles().some((cycle) => cycle.consumptionsFinalized));

  consumptionSummary = computed<BatchConsumptionSummaryRow[]>(() => {
    const estimatedItems = this.estimatedItemConsumptions();
    const estimatedWater = this.estimatedWaterUsage();
    const estimatedTotal = estimatedItems != null && estimatedWater != null ? estimatedItems + estimatedWater : null;
    const row = (
      labelKey: string,
      fallback: string,
      unit: string,
      estimated: number | null,
      actual: number,
    ): BatchConsumptionSummaryRow => ({
      labelKey,
      fallback,
      unit,
      estimated,
      actual,
      variance: this.hasFinalizedConsumptions() ? getConsumptionVariance(estimated, actual) : null,
    });

    return [
      row('batchDetails.chemicals', 'Chemicals', 'kg', estimatedItems, this.totalItemConsumptions()),
      row('batchDyeingCycle.waterUsageShort', 'Water Usage', 'L', estimatedWater, this.totalWaterUsage()),
      row('Total', 'Total', 'kg', estimatedTotal, this.totalActual()),
    ];
  });

  canStartDyeingCycle = computed(() => {
    const batch = this.batch();
    if (!this.canEdit() || !batch || batch.status !== BatchStatus.InProduction) return false;
    return this.dyeingCycles().every((cycle) => cycle.consumptionsFinalized);
  });

  dyeingCyclesStatusTag = computed<TagView>(() => {
    const cycles = this.dyeingCycles();
    if (cycles.length === 0) {
      return { label: this.localeHelperService.translateWithFallback('batchDetails.noCycles', 'No cycles'), severity: 'secondary' };
    }
    if (cycles.some((cycle) => cycle.startTime && !cycle.removedTime && !cycle.endTime)) {
      return { label: this.localeHelperService.translateWithFallback('In_Progress', 'In Progress'), severity: 'info' };
    }
    if (cycles.every((cycle) => cycle.colorStatusApproval?.isApproved === true)) {
      return { label: this.localeHelperService.translateWithFallback('batchDetails.allApproved', 'All Approved'), severity: 'success' };
    }
    if (cycles.some((cycle) => cycle.colorStatusApproval?.isApproved === false)) {
      return { label: this.localeHelperService.translateWithFallback('batchDetails.hasRejections', 'Has Rejections'), severity: 'warn' };
    }
    return {
      label: this.localeHelperService.translateWithFallback('batchDetails.cycleCount', `${cycles.length} cycles`, { count: cycles.length }),
      severity: 'secondary',
    };
  });

  dyeingCycleRows = computed<BatchDyeingCycleRow[]>(() =>
    this.dyeingCycles().map((cycle) => ({
      cycle,
      approval: this.cycleApprovalTag(cycle),
      isCompleted: cycle.consumptionsFinalized,
    })),
  );

  requisitionRows = computed<BatchRequisitionRow[]>(() =>
    this.requisitions().map((requisition) => ({ requisition, status: this.requisitionStatusTag(requisition.status) })),
  );

  canCreateRequisition = computed(() => {
    const batch = this.batch();
    if (!this.canEdit() || !this.productionWorkOrder() || !batch) return false;
    return batch.status === BatchStatus.ReadyForProduction || batch.status === BatchStatus.InProduction;
  });

  ngOnInit(): void {
    this.setupStatusListener();
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params['id'];
      const pwoId = params['pwoId'];

      if (!id || id === 'new') {
        this.isNewRecord.set(true);
        if (pwoId) {
          this.loadPwoForCreate(pwoId);
        }
      } else {
        this.isNewRecord.set(false);
        this.loadBatch(id);
      }
    });
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private cycleApprovalTag(cycle: ProductionCardDyeingCycle): TagView {
    if (!cycle.endTime) {
      return { label: this.localeHelperService.translateWithFallback('In_Progress', 'In Progress'), severity: 'info' };
    }
    if (!cycle.colorStatusApproval) {
      return { label: this.localeHelperService.translateWithFallback('batchDetails.pendingQA', 'Pending QA'), severity: 'warn' };
    }
    return cycle.colorStatusApproval.isApproved
      ? { label: this.localeHelperService.translateWithFallback('Approved', 'Approved'), severity: 'success' }
      : { label: this.localeHelperService.translateWithFallback('Rejected', 'Rejected'), severity: 'danger' };
  }

  private requisitionStatusTag(status: RequisitionStatus): TagView {
    switch (status) {
      case RequisitionStatus.Requested:
        return { label: this.localeHelperService.translateWithFallback('Requested', 'Requested'), severity: 'warn' };
      case RequisitionStatus.Processing:
        return { label: this.localeHelperService.translateWithFallback('Processing', 'Processing'), severity: 'info' };
      case RequisitionStatus.InRoute:
        return { label: this.localeHelperService.translateWithFallback('In_Route', 'In Route'), severity: 'info' };
      case RequisitionStatus.Received:
        return { label: this.localeHelperService.translateWithFallback('Received', 'Received'), severity: 'success' };
      case RequisitionStatus.Rejected:
        return { label: this.localeHelperService.translateWithFallback('Rejected', 'Rejected'), severity: 'danger' };
      case RequisitionStatus.Voided:
        return { label: this.localeHelperService.translateWithFallback('Voided', 'Voided'), severity: 'danger' };
      default:
        return { label: String(status), severity: 'secondary' };
    }
  }

  private setupStatusListener(): void {
    this.status.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newStatus) => {
      const current = this.batch()?.status;
      if (newStatus === null || current === undefined || newStatus === current) return;
      this.confirmChangeStatus(newStatus);
    });
  }

  private statusLabelFor(status: BatchStatus): string {
    return this.statusOptions[status] ?? BatchStatus[status] ?? String(status);
  }

  private confirmChangeStatus(target: BatchStatus): void {
    const batch = this.batch();
    if (!batch) return;

    const previousStatus = batch.status;
    const revertStatus = () => this.status.setValue(previousStatus, { emitEvent: false });
    const targetLabel = this.statusLabelFor(target);
    const currentLabel = this.statusLabelFor(previousStatus);

    // Not confirmAction(): that helper cannot target the 'general-dialog' p-confirmDialog this page uses.
    this.confirmationService.confirm({
      key: 'general-dialog',
      header: this.localeHelperService.translateWithFallback('Batch_Status_Confirm_Header', 'Change status'),
      message: this.localeHelperService.translateWithFallback(
        'Batch_Status_Confirm_Message',
        `Change the status from ${currentLabel} to ${targetLabel}?`,
        { from: currentLabel, to: targetLabel },
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.localeHelperService.translateWithFallback('Yes', 'Yes'),
      rejectLabel: this.localeHelperService.translateWithFallback('No', 'No'),
      accept: () => {
        this.incrementLoading();
        this.batchProxyService
          .updateStatus(batch.id, target)
          .pipe(
            switchMap((response) => {
              if (!response.success || !response.data) {
                revertStatus();
                return EMPTY;
              }
              GlobalMessageUtil.success(
                this.localeHelperService.translateWithFallback('Success', 'Success'),
                this.localeHelperService.translateWithFallback('Batch_Msg_Status_Updated', `Status changed to ${targetLabel}`, {
                  status: targetLabel,
                }),
              );
              return this.batchService.reloadBatch(batch.id);
            }),
            finalize(() => this.decrementLoading()),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({ error: () => revertStatus() });
      },
      reject: () => revertStatus(),
    });
  }

  private loadPwoForCreate(pwoId: string): void {
    this.incrementLoading();
    this.pwoProxyService
      .getById(pwoId)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response.success && response.data) {
          this.sourcePwo.set(response.data);
        }
      });
  }

  onRecipeItemSelect(item: ProductionWorkOrderItem): void {
    this.selectedRecipeItem.set(item);
    this.createForm.patchValue({
      productDevelopmentNumber: item.productRecipeNumber,
      plannedQuantity: item.quantity,
    });
  }

  private loadBatch(id: string): void {
    this.incrementLoading();
    this.batchService
      .initialize(id)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  save(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const pwo = this.sourcePwo();
    if (!pwo) {
      GlobalMessageUtil.error(
        this.localeHelperService.translateWithFallback('Error', 'Error'),
        this.localeHelperService.translateWithFallback('batchDetails.noPwoContext', 'No Production Work Order context'),
      );
      return;
    }

    const formValues = this.createForm.getRawValue();
    const command: BatchInsert = {
      status: BatchStatus.Draft,
      type: BatchType.Chemical,
      productDevelopmentNumber: formValues.productDevelopmentNumber!,
      uom: BATCH_UOM,
      initiator: {
        reference: pwo.id,
        entityDefinitionCode: ENTITY_DEF_PRODUCTION_WORK_ORDER,
      },
      plannedQuantity: formValues.plannedQuantity!,
    };

    this.isSaving.set(true);
    this.incrementLoading();
    this.batchProxyService
      .create(command)
      .pipe(
        finalize(() => {
          this.isSaving.set(false);
          this.decrementLoading();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response.success && response.data) {
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('Success', 'Success'),
            this.localeHelperService.translateWithFallback('batchDetails.batchCreated', `Batch ${response.data.number} created`, {
              number: response.data.number,
            }),
          );
          this.router.navigate(['/batch-details'], { queryParams: { id: response.data.id } });
        }
      });
  }

  openDyeingCycleModal(cycle?: ProductionCardDyeingCycle): void {
    const batch = this.batch();
    if (!batch) return;

    this.modalService
      .open(
        BatchDyeingCycleModalComponent,
        { modalClass: 'modal_large' },
        {
          batchId: batch.id,
          batchNumber: batch.number,
          cycle: cycle ?? null,
          recipeIngredients: this.productRecipe()?.ingredients ?? [],
          // The requisitions' lot items, not the batch's output containers in lotItems().
          batchLotItems: this.requisitions().flatMap((requisition) => requisition.lotItems ?? []),
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      // Reloads on any close: each step in the modal is saved as it happens, so a cancel can still leave changes.
      .subscribe(() => this.reloadBatchAndDyeingCycles());
  }

  // The batch too: unloading a cycle with approved QC completes it in the API.
  private reloadBatchAndDyeingCycles(): void {
    const batch = this.batch();
    if (!batch) return;

    this.incrementLoading();
    forkJoin([this.batchService.reloadBatch(batch.id), this.batchService.reloadDyeingCycles(batch.id)])
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  openCreateLotItemModal(): void {
    const batch = this.batch();
    if (!batch) return;

    if (!batch.manufacturerLotNumber || !batch.expiryDate) {
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('Warning', 'Warning'),
        this.localeHelperService.translateWithFallback(
          'Batch.fields_required_before_lot_item',
          'Set and save the Manufacturer Lot Number and Expiry Date before creating a lot item.',
        ),
      );
      return;
    }

    this.modalService
      .open(CreateLotItemModalComponent, {}, { batchId: batch.id, productDevelopmentNumber: batch.productDevelopmentNumber })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.action === 'save') {
          this.reloadLotItems();
        }
      });
  }

  private reloadLotItems(): void {
    const batch = this.batch();
    if (!batch) return;

    this.incrementLoading();
    this.batchService
      .reloadLotItems(batch.id)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  saveBatchFields(): void {
    const batch = this.batch();
    if (!batch || !this.canEditBatchFields()) return;

    const manufacturerLotNumber = this.manufacturerLotNumber.value?.trim();
    if (!manufacturerLotNumber) {
      this.manufacturerLotNumber.markAsDirty();
      GlobalMessageUtil.warn(
        this.localeHelperService.translateWithFallback('Warning', 'Warning'),
        this.localeHelperService.translateWithFallback('Batch.manufacturer_lot_number_required', 'Manufacturer Lot Number is required.'),
      );
      return;
    }
    const expiryDate = this.expiryDate.value ?? undefined;

    this.incrementLoading();
    this.batchProxyService
      .update(batch.id, { ...batch, manufacturerLotNumber, expiryDate })
      .pipe(
        switchMap((response) => {
          if (!response.success) return EMPTY;
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('Success', 'Success'),
            this.localeHelperService.translateWithFallback('Batch.batch_fields_saved', 'Saved'),
          );
          return this.batchService.reloadBatch(batch.id);
        }),
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  cancel(): void {
    this.router.navigate(['/batches']);
  }

  goToProductionWorkOrders(): void {
    this.router.navigate(['/production-work-orders']);
  }

  goToProductionWorkOrder(): void {
    const pwo = this.productionWorkOrder() ?? this.sourcePwo();
    if (!pwo) return;
    this.router.navigate(['/production-work-order-details'], { queryParams: { id: pwo.id } });
  }

  goToCreateRequisition(): void {
    const batch = this.batch();
    const pwo = this.productionWorkOrder();
    if (!batch || !pwo) return;
    navigateCrossDomain(
      {
        domain: 'inventory',
        path: 'requisition-details',
        queryParams: { id: 'new', pwoId: pwo.id, batchId: batch.id },
      },
      this.userAuthenticationService.getStoredJwtToken(),
    );
  }
}
